/**
 * GlobalView — Auth Store
 *
 * Zustand store managing authentication state (§25).
 * Tracks user profile, status (PENDING/ACTIVE/SUSPENDED/REJECTED),
 * and role (Viewer/Analyst/Admin).
 */

import { create } from 'zustand';
import type { UserRole, UserStatus, UserProfile } from '@globalview/shared';
import { signInWithGoogle, signOut as authSignOut, onAuthChange, getTokenClaims } from '../services/auth';
import { apiRequest } from '../services/api';

export type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'unauthenticated';

interface AuthState {
  /** UI-level auth status */
  authStatus: AuthStatus;

  /** User profile from backend */
  user: UserProfile | null;

  /** User's application status from custom claims */
  userStatus: UserStatus | null;

  /** User's role from custom claims */
  role: UserRole | null;

  /** Error message if auth fails */
  error: string | null;

  /** Sign in with Google */
  signIn: () => Promise<void>;

  /** Sign out */
  signOut: () => Promise<void>;

  /** Initialize auth listener — call once on app mount */
  initialize: () => () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  authStatus: 'idle',
  user: null,
  userStatus: null,
  role: null,
  error: null,

  signIn: async () => {
    set({ authStatus: 'loading', error: null });
    try {
      const firebaseUser = await signInWithGoogle();

      // Register user in backend (creates Firestore doc if not exists)
      try {
        await apiRequest<UserProfile>('/auth/register', {
          method: 'POST',
          body: {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoUrl: firebaseUser.photoURL,
          },
        });
      } catch {
        // Registration might fail if user already exists — that's OK
      }

      // Fetch claims and profile
      const claims = await getTokenClaims();
      const userStatus = (claims?.status as UserStatus) || 'pending';
      const role = (claims?.role as UserRole) || 'viewer';

      let profile: UserProfile | null = null;
      try {
        profile = await apiRequest<UserProfile>('/auth/me');
      } catch {
        // Profile fetch may fail for pending users, use Firebase data
        profile = {
          uid: firebaseUser.uid,
          email: firebaseUser.email || '',
          displayName: firebaseUser.displayName || undefined,
          photoUrl: firebaseUser.photoURL || undefined,
          status: userStatus,
          role,
          createdAt: new Date().toISOString(),
        };
      }

      set({
        authStatus: 'authenticated',
        user: profile,
        userStatus,
        role,
        error: null,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign in failed';
      set({ authStatus: 'unauthenticated', error: message });
    }
  },

  signOut: async () => {
    try {
      await authSignOut();
      set({
        authStatus: 'unauthenticated',
        user: null,
        userStatus: null,
        role: null,
        error: null,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign out failed';
      set({ error: message });
    }
  },

  initialize: () => {
    set({ authStatus: 'loading' });

    const unsubscribe = onAuthChange(async (firebaseUser) => {
      if (!firebaseUser) {
        set({
          authStatus: 'unauthenticated',
          user: null,
          userStatus: null,
          role: null,
        });
        return;
      }

      // Read custom claims as fallback
      const claims = await getTokenClaims();
      let userStatus = (claims?.status as UserStatus) || 'pending';
      let role = (claims?.role as UserRole) || 'viewer';

      let profile: UserProfile | null = get().user;
      if (!profile || profile.uid !== firebaseUser.uid) {
        try {
          profile = await apiRequest<UserProfile>('/auth/me');
          // API profile is the source of truth — override token claims
          if (profile.status) {
            userStatus = profile.status;
          }
          if (profile.role) {
            role = profile.role;
          }
        } catch {
          profile = {
            uid: firebaseUser.uid,
            email: firebaseUser.email || '',
            displayName: firebaseUser.displayName || undefined,
            photoUrl: firebaseUser.photoURL || undefined,
            status: userStatus,
            role,
            createdAt: new Date().toISOString(),
          };
        }
      }

      set({
        authStatus: 'authenticated',
        user: profile,
        userStatus,
        role,
      });
    });

    return unsubscribe;
  },
}));
